/**
 * CartContext - Global Shopping Cart State Management
 * Phase C2: Cart Frontend State
 * 
 * Features:
 * - Guest cart support via localStorage sessionId
 * - Automatic cart merge on login
 * - Real-time sync with backend API
 * - Optimistic UI updates
 * - Error handling and recovery
 */

import React, { createContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext();

// Generate unique session ID for guest users
const getSessionId = () => {
  let sessionId = localStorage.getItem('ogestyle_cart_session');
  if (!sessionId) {
    sessionId = 'guest_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('ogestyle_cart_session', sessionId);
  }
  return sessionId;
};

// Get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('ogestyle_token');
  const headers = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    // Guest user - include session ID
    headers['x-session-id'] = getSessionId();
  }
  
  return headers;
};

// Get API base URL
const getApiBaseUrl = () => {
  return process.env.REACT_APP_API_BASE_URL || 'https://api.ogestyle.com';
};

const normalizeAddToCartInput = (productOrPayload, quantityOrType = 1, legacyQuantity = 1) => {
  if (productOrPayload && typeof productOrPayload === 'object') {
    if (productOrPayload.productId) {
      return {
        ...productOrPayload,
        quantity: productOrPayload.quantity || legacyQuantity || 1,
      };
    }

    return {
      productId: productOrPayload._id,
      quantity: typeof legacyQuantity === 'number'
        ? legacyQuantity
        : typeof quantityOrType === 'number'
          ? quantityOrType
          : 1,
      shopId: productOrPayload.shopId?._id || productOrPayload.shopId,
    };
  }

  return {
    productId: productOrPayload,
    quantity: typeof quantityOrType === 'number' ? quantityOrType : 1,
  };
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [itemCount, setItemCount] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [reservedBookingsCount, setReservedBookingsCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Update derived state when cart items change
  useEffect(() => {
    const count = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    const total = cartItems.reduce((sum, item) => sum + (item.priceAtAdd * item.quantity), 0);
    setItemCount(count);
    setTotalPrice(total);
  }, [cartItems]);

  // Fetch cart from backend
  const refreshCart = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${getApiBaseUrl()}/api/cart`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }

      const data = await response.json();
      setCartItems(data.cart?.items || []);
      
      // Fetch reserved bookings count if authenticated
      const token = localStorage.getItem('ogestyle_token');
      if (token) {
        try {
          const bookingsResponse = await fetch(
            `${getApiBaseUrl()}/api/bookings/my-bookings?status=reserved`,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );
          if (bookingsResponse.ok) {
            const bookingsData = await bookingsResponse.json();
            setReservedBookingsCount(bookingsData.bookings?.length || 0);
          }
        } catch (bookingErr) {
          setReservedBookingsCount(0);
        }
      } else {
        setReservedBookingsCount(0);
      }
    } catch (err) {
      setError(err.message);
      // Don't clear cart on error - keep local state
    } finally {
      setLoading(false);
    }
  }, []);

  // Load cart on mount and when user logs in
  useEffect(() => {
    refreshCart();
    
    // Listen for cart migration event after login
    const handleCartMigration = () => {
      refreshCart();
    };
    
    window.addEventListener('cartMigrated', handleCartMigration);
    
    return () => {
      window.removeEventListener('cartMigrated', handleCartMigration);
    };
  }, [refreshCart]);

  // Add item to cart
  const addToCart = useCallback(async (productOrPayload, quantityOrType = 1, legacyQuantity = 1) => {
    try {
      setLoading(true);
      setError(null);

      const payload = normalizeAddToCartInput(
        productOrPayload,
        quantityOrType,
        legacyQuantity,
      );

      const response = await fetch(`${getApiBaseUrl()}/api/cart/add`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add item to cart');
      }

      const data = await response.json();
      setCartItems(data.cart?.items || []);
      
      return { success: true, cart: data.cart };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Add package to cart
  const addPackageToCart = useCallback(async (packageId, shopId, options = {}) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${getApiBaseUrl()}/api/cart/add-package`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ packageId, shopId, options }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add package to cart');
      }

      const data = await response.json();
      setCartItems(data.cart?.items || []);
      
      return { success: true, cart: data.cart };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Update item quantity
  const updateQuantity = useCallback(async (itemId, quantity) => {
    try {
      setLoading(true);
      setError(null);

      // Optimistic update
      const previousItems = [...cartItems];
      setCartItems(prevItems =>
        prevItems.map(item =>
          item._id === itemId ? { ...item, quantity } : item
        )
      );

      const response = await fetch(`${getApiBaseUrl()}/api/cart/item/${itemId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ quantity }),
      });

      if (!response.ok) {
        // Revert optimistic update
        setCartItems(previousItems);
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update quantity');
      }

      const data = await response.json();
      setCartItems(data.cart?.items || []);
      
      return { success: true, cart: data.cart };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [cartItems]);

  // Remove item from cart
  const removeItem = useCallback(async (itemId) => {
    try {
      setLoading(true);
      setError(null);

      // Optimistic update
      const previousItems = [...cartItems];
      setCartItems(prevItems => prevItems.filter(item => item._id !== itemId));

      const response = await fetch(`${getApiBaseUrl()}/api/cart/item/${itemId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        // Revert optimistic update
        setCartItems(previousItems);
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to remove item');
      }

      const data = await response.json();
      setCartItems(data.cart?.items || []);
      
      return { success: true, cart: data.cart };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [cartItems]);

  // Clear all items from cart
  const clearCart = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Optimistic update
      const previousItems = [...cartItems];
      setCartItems([]);

      const response = await fetch(`${getApiBaseUrl()}/api/cart/clear`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        // Revert optimistic update
        setCartItems(previousItems);
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to clear cart');
      }

      const data = await response.json();
      setCartItems(data.cart?.items || []);
      
      return { success: true, cart: data.cart };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [cartItems]);

  // Merge guest cart into user cart after login
  const mergeCart = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const sessionId = localStorage.getItem('ogestyle_cart_session');
      if (!sessionId) {
        // No guest cart to merge
        return { success: true, merged: false };
      }

      const response = await fetch(`${getApiBaseUrl()}/api/cart/merge`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ sessionId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to merge cart');
      }

      const data = await response.json();
      setCartItems(data.cart?.items || []);
      
      // Clear guest session ID after successful merge
      localStorage.removeItem('ogestyle_cart_session');
      
      return { success: true, merged: true, cart: data.cart };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const value = {
    // State
    cartItems,
    itemCount,
    totalPrice,
    reservedBookingsCount,
    loading,
    error,
    
    // Operations
    addToCart,
    addPackageToCart,
    updateQuantity,
    removeItem,
    clearCart,
    refreshCart,
    mergeCart,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;

